// P-PATHHOME-BUG-2-ROUND-2 — canonical persistence identity (pure core).
//
// THE BUG THIS FIXES (root cause, traced via code + DB schema):
//   child_attempts.child_id has a FK → students(id) and an RLS
//   WITH CHECK (child_id IN <parent's students>). The server generates
//   students.id (gen_random_uuid()); the app stores it on the local
//   child as `supabaseStudentId`. But every session screen wrote
//   child_attempts.child_id = the LOCAL activeChildId (a client-side
//   `c_<ts>_<rand>` uuid that is NEVER equal to any students.id). So
//   every authenticated insert FAILED the FK/RLS and was dropped after
//   retries — the entire persisted-attempt engine (trail, mastery,
//   skill graph, adaptive selection, parent dashboard) silently ran on
//   ZERO rows for real users. finalizeSession (the `sessions` aggregate)
//   already used supabaseStudentId; child_attempts did not — that
//   asymmetry is the smoking gun.
//
// THE FIX: route ALL child_attempts reads + writes through the resolved
// `supabaseStudentId` (the cloud students.id) instead of the local
// activeChildId. This module is the PURE decision core: given the
// current link + auth/load state, decide what to do. The async side
// effects (lazy-create + link, idempotency) live in useActiveStudentId.
//
// No data migration is needed: because the FK rejected every bad-local-id
// insert, NO orphaned child_attempts rows were ever written — the table
// is simply empty for real users. Fixing the id makes future writes land;
// there is nothing to backfill.

/**
 * @typedef {Object} ResolveInput
 * @property {string|null|undefined} activeSupabaseStudentId  active child's cloud students.id link (null = unlinked)
 * @property {{id:string}|null|undefined} user                Supabase auth user (null = not signed in)
 * @property {boolean} cloudLoading                            true while the cloud students list is still loading
 * @property {{id:string, kioskClaimedStudentId?:string|null}|null|undefined} activeChild  the active local registry child (null = none)
 *
 * @typedef {Object} ResolveAction
 * @property {"ready"|"pending"|"unavailable"|"create"} kind
 * @property {string} [id]      present when kind==="ready"
 * @property {string} [reason]  present for pending/unavailable (observability)
 */

/**
 * Decide how to resolve the canonical persistence id for the active child.
 *
 *   ready       — already linked; persist/read with action.id (the cloud
 *                 students.id). The common, no-op-cost path.
 *   pending     — auth/cloud still settling; DO NOT lazy-create yet (the
 *                 auto-link-by-name pass may still set the link). Caller
 *                 skips this attempt; the next one re-resolves.
 *   unavailable — no signed-in user, or no active child. Can't persist.
 *   create      — genuinely unlinked AFTER load settled → lazy-create a
 *                 cloud student and link it (handled by the hook).
 *
 * Pure + deterministic — no Date, no I/O — so the load-gating contract
 * (watch-out: "only auto-create when genuinely null, not stale/unloaded")
 * is unit-testable in isolation.
 *
 * @param {ResolveInput} input
 * @returns {ResolveAction}
 */
export function resolveStudentIdAction({
  activeSupabaseStudentId,
  user,
  cloudLoading,
  activeChild,
}) {
  // Already linked — the FK-valid id. Return it verbatim (never the local id).
  if (activeSupabaseStudentId) {
    return { kind: "ready", id: activeSupabaseStudentId };
  }
  // MA-288 classroom-roster-identity-write — a kiosk-CLAIMED kid deliberately
  // carries NO supabaseStudentId (kioskIdentity.ts's ensureKioskChildForEntry
  // sets it to null on purpose — see that module's header): useCloudSync +
  // this hook's own lazy-create path must stay OFF for it, because the real
  // students.id it should write under belongs to a DIFFERENT auth user (the
  // enrollment's claiming parent, not the signed-in teacher) — lazy-creating
  // OR consent-gating here would be wrong on both counts. Resolve straight to
  // kioskClaimedStudentId instead, exactly as "already linked" above, so no
  // create ever fires for this child. School-authority consent (MA-60,
  // `school_authority_v1`) already covers classroom use of this identity —
  // the founder's 2026-08-22 GO on MA-288 explicitly folds this write path
  // under that existing consent lane, not a new one. attemptLogger.ts's
  // resolveKioskAttemptRoute is what then routes the actual write through
  // the narrow insert_kiosk_child_attempt RPC instead of the plain upsert —
  // this function only decides WHICH id to write under, never how.
  if (activeChild && activeChild.kioskClaimedStudentId) {
    return { kind: "ready", id: activeChild.kioskClaimedStudentId };
  }
  // Not signed in (or auth not loaded) — no parent to own a students row.
  if (!user || !user.id) {
    return { kind: "unavailable", reason: "no-user" };
  }
  // Cloud list still loading — the auto-link-by-name pass runs once it
  // resolves and may set the link. Decide "genuinely null" only AFTER
  // it settles, so we don't race-create a duplicate student.
  if (cloudLoading) {
    return { kind: "pending", reason: "cloud-loading" };
  }
  // No active child to attach the cloud row to.
  if (!activeChild || !activeChild.id) {
    return { kind: "unavailable", reason: "no-active-child" };
  }
  // Signed in, loaded, has an active child, still no link → lazy-create.
  return { kind: "create" };
}
